import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import instruction1 from '../assets/dsmAssets/instruction1.svg';
import instruction2 from '../assets/dsmAssets/instruction2.svg';
import instruction3 from '../assets/dsmAssets/instruction3.svg';
import instruction4 from '../assets/dsmAssets/instruction4.svg';

const images = [instruction1, instruction2, instruction3, instruction4];
const imageTexts = [
    "Lift the iPad from the station",
    "Lift the iPad from the station",
    "Hover over the graphic icons on the printed surface",
    "The scanned icon will project the Augmented Reality Experience on the screen"
];

interface HowToInteractProps {
    onBack: () => void;
    onExplore: () => void;
}

const buttonStyle = {
    color: 'white',
    fontStyle: 'italic',
    fontSize: { xs: '2.5vw', sm: '2vw', md: '1.5vw' },
    padding: '1.5vh 4vw',
    border: '1px solid white',
    borderRadius: '50px',
    background: 'transparent',
    '&:hover': {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid white',
    },
};

const HowToInteract: React.FC<HowToInteractProps> = ({ onBack, onExplore }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [scale, setScale] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);

    useEffect(() => {
        setScale(1);

        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight') {
                setCurrentIndex(prev => Math.min(prev + 1, images.length - 1));
            } else if (event.key === 'ArrowLeft') {
                setCurrentIndex(prev => Math.max(prev - 1, 0));
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.touches[0].clientX);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStart === null) return;
        
        const touchEnd = e.changedTouches[0].clientX;
        const diff = touchStart - touchEnd;

        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                setCurrentIndex(prev => Math.min(prev + 1, images.length - 1));
            } else {
                setCurrentIndex(prev => Math.max(prev - 1, 0));
            }
        }
        setTouchStart(null);
    };

    return (
        <Box sx={{
            // width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            px: { xs: 2, sm: 4, md: 6 },
        }}>
            <Typography 
                variant="h1" 
                sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '5vw', sm: '4vw', md: '3vw' },
                    marginTop: { xs: '10vh', sm: '10vh', md: '10vh' },
                    textAlign: 'center',
                }}
            >
                How to interact
            </Typography>
            
            <Box 
                sx={{
                    width: { xs: '80vw', sm: '60vw', md: '47vw' },
                    height: { xs: '45vh', sm: '40vh', md: '40vh' },
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: { xs: '2vh', sm: '3vh' },
                    transform: `scale(${scale})`,
                    transition: 'transform 1s ease-in-out',
                    cursor: 'pointer',
                }}
                onClick={() => setCurrentIndex(prev => 
                    prev === images.length - 1 ? 0 : prev + 1
                )}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setCurrentIndex(prev => 
                            prev === images.length - 1 ? 0 : prev + 1
                        );
                    }
                }}
            >
                <Box
                    component="img"
                    src={images[currentIndex]}
                    alt={`Instruction ${currentIndex + 1}`}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        transition: 'opacity 0.8s ease-in-out',
                    }}
                />
            </Box>

            <Typography 
                sx={{
                    color: 'white',
                    fontStyle: 'italic',
                    fontSize: { xs: '5.5vw', sm: '2.5vw', md: '2vw' },
                    textAlign: 'center',
                    marginTop: { xs: '-2vh', sm: '3vh', md: '5vh' },
                    maxWidth: '80%',
                }}
            >
                {imageTexts[currentIndex]}
            </Typography>

            {/* Dot Navigation */}
            <Box sx={{
                display: 'flex',
                gap: '2vw',
                position: 'absolute',
                bottom: { xs: '18vh', sm: '24vh', md: '24vh' },
                left: '50%',
                transform: 'translateX(-50%)',
            }}>
                {images.map((_, index) => {
                    const size = { xs: '20px', sm: '24px', md: '28px' };
                    return (
                        <Box
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            sx={{
                                width: size,
                                height: size,
                                aspectRatio: '1',
                                borderRadius: '50%',
                                backgroundColor: currentIndex === index ? 'white' : 'gray',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.2)',
                                    backgroundColor: currentIndex === index ? 'white' : '#a0a0a0',
                                },
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setCurrentIndex(index);
                                }
                            }}
                        />
                    );
                })}
            </Box>

            {(currentIndex === 0 || currentIndex === 3) && (
                <Box sx={{
                    position: 'absolute',
                    bottom: { xs: '6vh', sm: '14vh', md: '16vh' },
                    width: '80%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: '2vw',
                }}>
                    <Button 
                        variant="outlined"
                        onClick={onBack}
                        sx={{
                            ...buttonStyle,
                            // ml: '-2vw',
                        }}
                    >
                        Back
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={onExplore}
                        sx={{
                            ...buttonStyle,
                            // mr: '2vw',
                        }}
                    >
                        Explore Now
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default HowToInteract;
